import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, PackageOpen } from "lucide-react";
import { HeroBanner } from "@/components/HeroBanner";
import { DualModelBanner } from "@/components/DualModelBanner";
import { CategoryRail } from "@/components/CategoryRail";
import { CategoryBanner } from "@/components/CategoryBanner";
import { VirtualProductGrid } from "@/components/VirtualProductGrid";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { MaintenanceScreen } from "@/components/MaintenanceScreen";
import { ProductDetailDialog } from "@/components/ProductDetailDialog";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { getProducts, isSiteLive } from "@/lib/storage";
import { apiListBanners } from "@/lib/api";
import { clearCart } from "@/lib/cart";
import { HOME } from "@/constants/testIds";

export const Home = ({ cartOpen, onCartOpen, onCartClose }) => {
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [buyTarget, setBuyTarget] = useState(null); // single product OR { items: [...] }
  const [detailTarget, setDetailTarget] = useState(null);
  const [live, setLive] = useState(false);

  const reload = async () => {
    try {
      const [p, l, b] = await Promise.all([
        getProducts(),
        isSiteLive(),
        apiListBanners(),
      ]);
      setProducts(p);
      setLive(l);
      setBanners(b);
    } catch {
      /* keep last-known state */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);
    window.addEventListener("mb-maintenance-change", reload);
    window.addEventListener("mb-categories-change", reload);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("mb-maintenance-change", reload);
      window.removeEventListener("mb-categories-change", reload);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    });
  }, [products, query, category]);

  const cats = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [products]);

  const handleBuyNow = (product) => {
    setDetailTarget(null);
    setBuyTarget({ product });
  };

  const handleCardBuyNow = (product) => {
    const sizes =
      Array.isArray(product.sizes) && product.sizes.length > 0
        ? product.sizes
        : ["Free Size"];
    if (sizes.length > 1) {
      setDetailTarget(product);
      return;
    }
    setBuyTarget({ product: { ...product, selectedSize: sizes[0] } });
  };

  const handleCheckoutCart = (items) => {
    onCartClose?.();
    setBuyTarget({ items });
  };

  const handleCheckoutDone = async ({ wasCart }) => {
    if (wasCart) clearCart();
    // Refresh products so stock counters reflect the new order immediately.
    await reload();
    // Auto-redirect to the permanent 'My Orders' screen — App.js listens for
    // this event and opens the MyOrdersDialog. Deferred a tick so the checkout
    // dialog's own close animation completes cleanly first.
    setTimeout(() => {
      window.dispatchEvent(new Event("mb-open-my-orders"));
    }, 250);
  };

  if (!live) {
    return (
      <>
        <MaintenanceScreen />
        <CartDrawer
          open={cartOpen}
          onClose={onCartClose}
          onCheckout={handleCheckoutCart}
        />
      </>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10 space-y-10 sm:space-y-14">
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <CategoryRail category={category} onChange={setCategory} />
      </motion.section>

      <CategoryBanner category={category} banners={banners} />

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <DualModelBanner />
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <HeroBanner />
      </motion.section>

      <section id="storefront" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.22em] text-[#064E3B] font-medium">
              Storefront
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-light text-[#0B0C10] tracking-tight">
              The full edit.
            </h2>
            <p className="text-sm text-neutral-600 max-w-md">
              {products.length} product{products.length === 1 ? "" : "s"} ·
              lazy-loaded · cash on delivery
            </p>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search
                size={16}
                strokeWidth={1.6}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500"
              />
              <input
                data-testid={HOME.searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the bazaar..."
                className="w-full bg-white/70 border border-white/60 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-full pl-10 pr-4 py-2.5 text-sm transition-all"
              />
            </div>
          </div>
        </div>

        {cats.length > 1 && (
          <div
            data-testid="home-category-rail"
            className="flex gap-1.5 overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6 py-1"
            style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
          >
            {cats.map((c) => (
              <button
                key={c}
                data-testid={HOME.categoryFilter(c)}
                onClick={() => setCategory(c)}
                className={`shrink-0 text-[10px] sm:text-[11px] uppercase tracking-[0.14em] font-medium px-3 py-1.5 rounded-full border transition-all whitespace-nowrap ${
                  category === c
                    ? "bg-[#0B0C10] text-white border-[#0B0C10]"
                    : "bg-white/60 text-[#0B0C10] border-white/60 hover:bg-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <EmptyState hasProducts={products.length > 0} loading={loading} />
        ) : (
          <VirtualProductGrid
            products={filtered}
            onBuy={handleCardBuyNow}
            onOpen={setDetailTarget}
          />
        )}
      </section>

      <ProductDetailDialog
        product={detailTarget}
        open={!!detailTarget}
        onClose={() => setDetailTarget(null)}
        onBuyNow={handleBuyNow}
        onOpenCart={() => {
          setDetailTarget(null);
          onCartOpen?.();
        }}
      />

      <CartDrawer
        open={cartOpen}
        onClose={onCartClose}
        onCheckout={handleCheckoutCart}
      />

      <CheckoutDialog
        target={buyTarget}
        open={!!buyTarget}
        onClose={() => setBuyTarget(null)}
        onPlaced={handleCheckoutDone}
      />

      <Footer />
    </div>
  );
};

const EmptyState = ({ hasProducts, loading }) => (
  <div
    data-testid="home-empty-state"
    className="rounded-3xl border border-white/60 bg-white/60 backdrop-blur-xl backdrop-saturate-150 p-10 sm:p-16 text-center shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
  >
    <div className="mx-auto w-16 h-16 rounded-full bg-[#FFF5D1] flex items-center justify-center mb-6">
      <PackageOpen size={28} strokeWidth={1.5} className="text-[#064E3B]" />
    </div>
    <h3 className="font-display text-2xl sm:text-3xl text-[#0B0C10] font-light mb-2">
      {loading
        ? "Loading the bazaar…"
        : hasProducts
          ? "No matches found."
          : "The bazaar is quiet."}
    </h3>
    <p className="text-sm text-neutral-600 max-w-sm mx-auto mb-6">
      {loading
        ? "Fetching the latest drops from the vault."
        : hasProducts
          ? "Try a different search or category."
          : "Fresh drops land soon. Check back in a bit."}
    </p>
  </div>
);
